import { Users, Crown, Pencil, Trash2 } from 'lucide-react';
import type { Team } from '../../types';
import { useApp } from '../../context/AppContext';
import Avatar, { AvatarGroup } from '../common/Avatar';

interface TeamCardProps {
  team: Team;
  onEdit: () => void;
  onDelete: () => void;
}

export default function TeamCard({ team, onEdit, onDelete }: TeamCardProps) {
  const { getUserById, state } = useApp();
  const lead = getUserById(team.leadId);
  const members = team.memberIds.map((id) => getUserById(id)).filter(Boolean);
  const teamTasks = state.tasks.filter((t) => {
    const board = state.boards.find((b) => b.teamId === team.id);
    return board && t.boardId === board.id;
  });
  const activeTasks = teamTasks.filter((t) => t.status === 'in_progress' || t.status === 'in_review').length;
  const completedTasks = teamTasks.filter((t) => t.status === 'done').length;

  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-5 hover:shadow-lg hover:border-brand-200 transition-all duration-300 group">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center"
            style={{ backgroundColor: `${team.color}15` }}
          >
            <Users size={20} style={{ color: team.color }} />
          </div>
          <div>
            <h3 className="text-base font-semibold text-slate-800 group-hover:text-brand-600 transition-colors">
              {team.name}
            </h3>
            <p className="text-xs text-slate-400">{members.length} membros</p>
          </div>
        </div>
        <div className="flex items-center gap-1 transition-opacity">
          <button
            onClick={(e) => { e.stopPropagation(); onEdit(); }}
            className="p-1.5 rounded-lg text-slate-400 hover:text-brand-500 hover:bg-brand-50 transition-colors"
          >
            <Pencil size={14} />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onDelete(); }}
            className="p-1.5 rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50 transition-colors"
          >
            <Trash2 size={14} />
          </button>
        </div>
      </div>

      <p className="text-sm text-slate-500 mb-4 line-clamp-2 leading-relaxed">
        {team.description}
      </p>

      {lead && (
        <div className="flex items-center gap-2 mb-4 px-3 py-2 bg-amber-50/60 rounded-xl">
          <Crown size={14} className="text-amber-500 shrink-0" />
          <Avatar name={lead.name} color={lead.avatarColor} size="sm" />
          <span className="text-xs font-medium text-slate-600">{lead.name}</span>
          <span className="text-[10px] text-amber-600 font-medium ml-auto">Líder</span>
        </div>
      )}

      <div className="flex items-center justify-between">
        <AvatarGroup
          users={members.map((m) => ({ name: m!.name, color: m!.avatarColor }))}
          max={4}
          size="md"
        />
        <div className="flex gap-3 text-xs">
          <span className="text-brand-600 font-medium">{activeTasks} ativas</span>
          <span className="text-emerald-600 font-medium">{completedTasks} concluídas</span>
        </div>
      </div>
    </div>
  );
}
